/**
 * Shop by Section
 * ----------------
 * Per-section behaviour:
 *   1. Condense: the sticky quick-nav morphs from image cards into pill buttons
 *      once the section's sentinel scrolls past the sticky line.
 *   2. Active state: highlights the pill matching the section currently in view.
 *   3. Smooth anchor scroll that accounts for the sticky nav height + offset.
 *
 * Nav cards and sections are paired by index — both are rendered in the same
 * block order — so scroll/active-state do not depend on block IDs matching
 * across contexts.
 *
 * Safe to run multiple times (Shopify theme editor re-renders sections); each
 * root is initialised once via a data flag.
 */
(function () {
  'use strict';

  function initRoot(root) {
    if (!root || root.dataset.sbsInit === 'true') return;
    root.dataset.sbsInit = 'true';

    var quicknav = root.querySelector('[data-sbs-quicknav]');
    var sentinel = root.querySelector('[data-sbs-sentinel]');
    var navlinks = Array.prototype.slice.call(root.querySelectorAll('[data-sbs-navlink]'));
    var sections = Array.prototype.slice.call(root.querySelectorAll('[data-sbs-section]'));
    var stickyOffset = parseInt(root.getAttribute('data-sbs-sticky-offset'), 10) || 0;

    // While a nav click is smooth-scrolling we force the nav condensed and lock the
    // condense observer off, so the sticky nav doesn't resize mid-scroll and shift
    // the target section out from under us.
    var condenseLock = false;

    // Map each section element to its index so observers can resolve the nav card.
    var indexOfSection = new Map();
    sections.forEach(function (sec, i) {
      indexOfSection.set(sec, i);
    });

    // Populate each nav card from its Section. The parent section can't read nested
    // block settings in Liquid, so the title and featured image are sourced from
    // each rendered section's data-sbs-* attributes (paired by index — nav cards and
    // sections share the same block order).
    navlinks.forEach(function (link, i) {
      var sec = sections[i];
      if (!sec) return;

      // Title -> label
      var title = sec.getAttribute('data-sbs-title');
      if (title) {
        var label = link.querySelector('[data-sbs-navlabel]');
        if (label) label.textContent = title;
      }

      // Featured image -> nav card media (replaces the placeholder). alt is empty
      // because the label already provides the link's accessible name.
      var imgSrc = sec.getAttribute('data-sbs-img');
      var media = link.querySelector('[data-sbs-navmedia]');
      if (imgSrc && media && !media.querySelector('.sbs-navcard__img')) {
        var img = document.createElement('img');
        img.className = 'sbs-navcard__img';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.alt = '';
        var srcset = sec.getAttribute('data-sbs-img-srcset');
        if (srcset) {
          img.srcset = srcset;
          img.sizes = '(max-width: 749px) 40vw, 220px';
        }
        img.src = imgSrc;

        var scrim = media.querySelector('.sbs-navcard__scrim');
        media.insertBefore(img, scrim || null);

        var ph = media.querySelector('[data-sbs-navph]');
        if (ph) ph.remove();
      }
    });

    // 1. Condense the nav once the sentinel passes the sticky line.
    if (quicknav && sentinel && 'IntersectionObserver' in window) {
      var condenseObserver = new IntersectionObserver(
        function (entries) {
          if (condenseLock) return;
          entries.forEach(function (entry) {
            quicknav.classList.toggle('is-condensed', !entry.isIntersecting);
          });
        },
        // Fire when the sentinel crosses the sticky line (top of viewport + offset).
        { rootMargin: '-' + (stickyOffset + 1) + 'px 0px 0px 0px', threshold: 0 }
      );
      condenseObserver.observe(sentinel);
    }

    // 2. Active-link tracking based on which section is in view.
    function setActive(index) {
      navlinks.forEach(function (link, i) {
        link.classList.toggle('is-active', i === index);
      });
    }

    if (sections.length && 'IntersectionObserver' in window) {
      var visible = new Set();
      var activeObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) visible.add(entry.target);
            else visible.delete(entry.target);
          });
          // Choose the top-most section currently intersecting the focus band.
          var best = null;
          var bestTop = Infinity;
          visible.forEach(function (sec) {
            var top = sec.getBoundingClientRect().top;
            if (top < bestTop) {
              bestTop = top;
              best = sec;
            }
          });
          if (best) setActive(indexOfSection.get(best));
        },
        // Focus band roughly the upper third of the viewport.
        { rootMargin: '-30% 0px -60% 0px', threshold: [0, 0.01, 0.5, 1] }
      );
      sections.forEach(function (sec) {
        activeObserver.observe(sec);
      });
    }

    // 3. Keep the sticky nav clear of the site header. #header-component reveals on
    //    scroll-up; when it does, drop the nav by the header height so it sits below
    //    the header, otherwise pin it to the top of the viewport.
    var headerComponent = document.querySelector('#header-component');

    // Hard-coded header height: 65px on desktop, 59px on mobile (≤749px).
    function headerHeight() {
      return window.matchMedia('(max-width: 749px)').matches ? 59 : 65;
    }

    function headerRevealed() {
      return !!(headerComponent && headerComponent.getAttribute('data-scroll-direction') === 'up');
    }

    function applyNavTop() {
      if (quicknav) quicknav.style.top = (headerRevealed() ? headerHeight() : 0) + 'px';
    }

    if (headerComponent && 'MutationObserver' in window) {
      new MutationObserver(applyNavTop).observe(headerComponent, {
        attributes: true,
        attributeFilter: ['data-scroll-direction'],
      });
    }
    applyNavTop();

    // 4. Smooth scroll that clears the sticky chrome so the section title isn't
    //    hidden. We condense the nav first (its resting state after any scroll) so
    //    the sticky nav's height doesn't change mid-scroll and shift the target.
    function condenseNav() {
      if (quicknav) quicknav.classList.add('is-condensed');
    }

    function currentOffset(scrollingUp) {
      // The header only reveals when scrolling up, dropping the nav by the header
      // height; a downward scroll leaves the header hidden and the nav at the top.
      // Reserve whichever applies, plus the condensed nav height and a small gap.
      var headerReserve = scrollingUp && headerComponent ? headerHeight() : 0;
      var navHeight = quicknav ? quicknav.offsetHeight : 0;
      return headerReserve + navHeight + 12;
    }

    function releaseCondenseLockSoon() {
      var released = false;
      function release() {
        if (released) return;
        released = true;
        condenseLock = false;
        window.removeEventListener('scrollend', release);
      }
      window.addEventListener('scrollend', release);
      // Fallback in case scrollend never fires (older browsers / interrupted scroll).
      setTimeout(release, 800);
    }

    navlinks.forEach(function (link, i) {
      link.addEventListener('click', function (event) {
        var target = sections[i];
        if (!target) return;
        event.preventDefault();

        // Condense + lock BEFORE measuring so getBoundingClientRect reflects the
        // settled layout (the nav won't resize during the scroll).
        condenseLock = true;
        condenseNav();

        var rawTop = window.pageYOffset + target.getBoundingClientRect().top;
        var scrollingUp = rawTop < window.pageYOffset;
        var y = Math.max(0, rawTop - currentOffset(scrollingUp));
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' });
        setActive(i);
        releaseCondenseLockSoon();
      });
    });
  }

  function initAll() {
    document.querySelectorAll('[data-sbs]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when this section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector ? event.target.querySelector('[data-sbs]') : null;
    if (root) initRoot(root);
  });
})();
